import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { ClipboardList, Lock, AlertTriangle, Trophy, ChevronDown, ChevronRight } from "lucide-react";
import { Badge } from "../components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MyResultsPage() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feePending, setFeePending] = useState(false);
  const [studentPosition, setStudentPosition] = useState(null);
  const [activeTerm, setActiveTerm] = useState("Term 1");
  const [activeYear, setActiveYear] = useState("");
  const [announcements, setAnnouncements] = useState([]);
  const [expandedTerms, setExpandedTerms] = useState({});

  useEffect(() => {
    fetchAnnouncements();
    axios.get(`${API}/settings/active-term`).then(res => {
      const term = res.data.active_term || "Term 1";
      const year = res.data.academic_year || new Date().getFullYear().toString();
      setActiveTerm(term);
      setActiveYear(year);
      setExpandedTerms({ [`${term}-${year}`]: true });
      // Fetch results after we have the term info
      fetchMyResultsWithTerm(term, year);
    }).catch(() => {
      // Fallback: fetch results anyway
      fetchMyResultsWithTerm("Term 1", new Date().getFullYear().toString());
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStudentPosition = async (studentId, term) => {
    try {
      const response = await axios.get(
        `${API}/results/student-position/${studentId}/${term}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      setStudentPosition(response.data);
    } catch (error) {
      console.error("Failed to fetch student position:", error);
    }
  };

  const fetchAnnouncements = async () => {
    try {
      const res = await axios.get(`${API}/announcements`);
      setAnnouncements(res.data || []);
    } catch (error) {
      // Ignore error for now
    }
  };

  const fetchMyResultsWithTerm = async (term, year) => {
    try {
      const response = await axios.get(`${API}/results/my-results`);
      setResults(response.data);
      setFeePending(false);

      // Get student ID to fetch position
      if (response.data.length > 0) {
        const studentId = response.data[0]?.student_id;
        if (studentId) {
          fetchStudentPosition(studentId, term);
        }
      }
    } catch (error) {
      if (error.response?.data?.detail === "FEE_PENDING") {
        setFeePending(true);
      } else {
        toast.error("Failed to fetch results");
      }
    } finally {
      setLoading(false);
    }
  };

  const getUnitGrade = (percentage) => {
    if (percentage >= 90) return 1;
    if (percentage >= 80) return 2;
    if (percentage >= 70) return 3;
    if (percentage >= 60) return 4;
    if (percentage >= 50) return 5;
    return 6;
  };

  const getGradeColor = (grade) => {
    switch (grade) {
      case 1:
        return "bg-emerald-50 text-emerald-700";
      case 2:
        return "bg-blue-50 text-blue-700";
      case 3:
        return "bg-amber-50 text-amber-700";
      case 4:
        return "bg-orange-50 text-orange-700";
      case 5:
        return "bg-gray-50 text-gray-700";
      default:
        return "bg-red-50 text-red-700";
    }
  };

  // Group results by term and year
  const groupedResults = results.reduce((acc, r) => {
    const key = `${r.term}-${r.academic_year}`;
    if (!acc[key]) acc[key] = { term: r.term, year: r.academic_year, results: [] };
    acc[key].results.push(r);
    return acc;
  }, {});

  // Sort groups: current term on top, then reverse chronological
  const sortedGroups = Object.values(groupedResults).sort((a, b) => {
    const aIsCurrent = a.term === activeTerm && String(a.year) === String(activeYear);
    const bIsCurrent = b.term === activeTerm && String(b.year) === String(activeYear);
    if (aIsCurrent && !bIsCurrent) return -1;
    if (!aIsCurrent && bIsCurrent) return 1;
    if (a.year !== b.year) return b.year - a.year;
    return parseInt(b.term.replace("Term ", "")) - parseInt(a.term.replace("Term ", ""));
  });

  const toggleTerm = (key) => {
    setExpandedTerms(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Calculate stats for current term only
  const currentTermResults = results.filter(r =>
    r.term === activeTerm && String(r.academic_year) === String(activeYear)
  );
  const overallPercentage = currentTermResults.length > 0
    ? (currentTermResults.reduce((sum, r) => sum + r.percentage, 0) / currentTermResults.length).toFixed(1)
    : 0;

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  if (feePending) {
    return (
      <div data-testid="fee-wall" className="min-h-[400px] flex items-center justify-center">
        <Card className="max-w-md w-full border-slate-200 shadow-lg">
          <CardContent className="text-center py-12">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-pink-50 flex items-center justify-center">
              <Lock className="text-pink-500" size={40} />
            </div>
            <h2 className="font-heading font-bold text-2xl text-slate-900 mb-3">
              Results Locked
            </h2>
            <p className="text-slate-600 mb-6">
              Your academic results are currently unavailable because your fee payment is pending.
            </p>
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-center justify-center gap-2 text-amber-700">
                <AlertTriangle size={18} />
                <span className="font-medium">Fee Status: Pending</span>
              </div>
              <p className="text-sm text-amber-600 mt-2">
                Please contact the administration to update your fee status.
              </p>
              <p className="text-sm text-amber-700 mt-2 font-medium">
                If this is wrong, please contact the admin office.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="my-results-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-heading font-bold text-3xl text-slate-900 flex items-center gap-3">
          <ClipboardList className="text-pink-500" size={32} />
          My Results
        </h1>
        <p className="text-slate-500 mt-1">View your academic performance</p>
      </div>

      {/* Summary Card - Current Term Only */}
      {currentTermResults.length > 0 && (
        <Card className="border-slate-200 mb-6 border-l-4 border-l-pink-500">
          <CardContent className="py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
              <div>
                <p className="text-sm text-slate-500">{activeTerm} {activeYear} Performance</p>
                <p className="text-3xl font-heading font-bold text-slate-900">
                  {overallPercentage}%
                </p>
              </div>
              <div className="flex gap-6">
                <div className="text-center">
                  <p className="text-sm text-slate-500">Subjects</p>
                  <p className="text-xl font-bold text-slate-900">{currentTermResults.length}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-slate-500">Highest Score</p>
                  <p className="text-xl font-bold text-emerald-600">
                    {Math.max(...currentTermResults.map(r => r.percentage))}%
                  </p>
                </div>
                {studentPosition && (
                  <div className="text-center">
                    <p className="text-sm text-slate-500">Class Position</p>
                    <div className="flex items-center justify-center gap-2">
                      <Trophy className="w-5 h-5 text-amber-500" />
                      <p className="text-xl font-bold text-slate-900">
                        {studentPosition.position}/{studentPosition.total_students_in_class}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results by Term */}
      {results.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center text-slate-500">
            <ClipboardList size={48} className="mx-auto mb-4 opacity-50" />
            <p>No results available yet</p>
            <p className="text-sm">Your results will appear here once they are published</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {sortedGroups.map((group) => {
            const key = `${group.term}-${group.year}`;
            const isExpanded = expandedTerms[key];
            const isCurrent = group.term === activeTerm && group.year === activeYear;
            const groupAvg = (group.results.reduce((s, r) => s + r.percentage, 0) / group.results.length).toFixed(1);

            return (
              <Card key={key} className={`border-slate-200 ${isCurrent ? 'border-l-4 border-l-pink-500' : ''}`}>
                <CardHeader
                  className="cursor-pointer pb-3 hover:bg-slate-50 transition-colors"
                  onClick={() => toggleTerm(key)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                      <CardTitle className="font-heading text-lg">
                        {group.term} — {group.year}
                      </CardTitle>
                      {isCurrent && (
                        <Badge className="bg-pink-100 text-pink-700">Current</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span>{group.results.length} subjects</span>
                      <span className="font-semibold text-slate-700">{groupAvg}%</span>
                    </div>
                  </div>
                </CardHeader>
                {isExpanded && (
                  <CardContent>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50 hover:bg-slate-50">
                            <TableHead className="font-medium text-slate-500">Subject</TableHead>
                            <TableHead className="font-medium text-slate-500">Score</TableHead>
                            <TableHead className="font-medium text-slate-500">Percentage</TableHead>
                            <TableHead className="font-medium text-slate-500">Grade</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {group.results.map((result) => (
                            <TableRow key={result.id} className="table-row-hover">
                              <TableCell className="font-medium text-slate-900">{result.subject}</TableCell>
                              <TableCell className="text-slate-600">{result.score}/{result.max_score}</TableCell>
                              <TableCell className="text-slate-600">{result.percentage}%</TableCell>
                              <TableCell>
                                <Badge className={getGradeColor(getUnitGrade(result.percentage))}>
                                  {getUnitGrade(result.percentage)}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
